<script setup lang="ts">
import {
  computed,
  onMounted,
  ref,
} from 'vue';

import { useTimeoutFn } from '@vueuse/core';

import {
  ActionButton,
  ArrowBtn,
  Button,
  Collapse,
  FloatingTrigger,
  RecordGrid,
  Tooltip,
  TruncateList,
  useComputedSettings,
  useDeviceFeatures,
  useI18n,
  useTheme,
} from '@thehotelsnetwork/widgets-core';
import {
  type ComparisonWidgetBundle,
} from '@thehotelsnetwork/personalization-types';

import DiscountPrice from '../DiscountPrice/DiscountPrice.vue';
import Signature from '../Signature/Signature.vue';
import FloatingSectionHeader from '../FloatingSectionHeader/FloatingSectionHeader.vue';
import LearnMore from '../LearnMore/LearnMore.vue';
import FormHandler from '../FormHandler/FormHandler.vue';
import ContactForm from '../ContactForm/ContactForm.vue';
import EmailSearchForm from '../EmailSearchForm/EmailSearchForm.vue';
import PerksList from '../PerksList/PerksList.vue';
import SaveSearchBtn from '../SaveSearchBtn/SaveSearchBtn.vue';
import PriceMatchMsg from '../PriceMatchMsg/PriceMatchMsg.vue';
import GotoBeBtnText from '../GotoBeBtnText/GotoBeBtnText.vue';

import {
  usePriceMatch,
  useDiscountPrice,
  useHotelPrices,
  useCacheValues,
  useActions,
} from '@/composables';
import { useStyles } from '@/modules/styles';

import { PriceSection } from '@/declarations';
import { htmlEntityDecode } from '@/utils';

const props = defineProps<{
  config: ComparisonWidgetBundle;
  section: string;
  mode: 'desktop' | 'mobile';
  showPriceMatchOpen: boolean;
}>();

const emit = defineEmits([
  'close',
  'update:section',
]);

const rootElement = ref<HTMLElement | null>(null);
const { t } = useI18n();
const theme = useTheme();
const styles = useStyles();
const { isHoverCapable } = useDeviceFeatures();
const computedSettings = useComputedSettings();

const {
  applyDiscountAction,
  contactEmailHandler,
  saveSearchHandler,
  openBEHandler,
} = useActions(props.config.actions);

const {
  message: priceMatchMessage,
  showBtn: priceMatchShowBtn,
} = usePriceMatch(
  props.config.payload.priceMatch,
  applyDiscountAction,
);

function handleApplyDiscount() {
  if (applyDiscountAction) {
    // @ts-ignore
    applyDiscountAction();
  }
}

const showPriceMath = ref(props.showPriceMatchOpen);

const triggerPosition = computed(() => {
  if (
    props.config.devicePosition === 'UL'
    || props.config.devicePosition === 'UR'
  ) {
    return 'bottom';
  }
  return 'top';
});
const tooltipPosition = computed(() => {
  if (
    props.config.devicePosition === 'UR'
    || props.config.devicePosition === 'CR'
    || props.config.devicePosition === 'LR'
  ) {
    return 'top-end';
  }
  return 'top-start';
});

const discountPriceData = useDiscountPrice(
  props.config.payload.priceData,
);
const hotelPrices = useHotelPrices(
  props.config.payload.hotel,
);

const {
  values: cacheSaveSearchValue,
  reset: resetSaveSearchValues,
} = useCacheValues({
  email: '',
  terms: false,
});

useTimeoutFn(() => {
  if (priceMatchMessage) {
    showPriceMath.value = true;
  }
}, 1500, { immediate: true });

onMounted(() => {
  if (rootElement.value) {
    rootElement.value.focus();
  }
});
</script>

<template>
  <div
    ref="rootElement"
    class="vertical-content"
  >
    <Transition
      name="fade"
      mode="out-in"
    >
      <div
        v-if="props.section === PriceSection.INDEX"
      >
        <FloatingTrigger
          v-if="props.mode === 'desktop'"
          open
          :position="triggerPosition"
          @click="emit('close')"
          class="h-[38px] font-bold"
        >
          <h1 class="font-semibold text-s">
            {{ t('Compare Prices') }}
          </h1>
        </FloatingTrigger>
        <div
          v-else
          class="flex justify-end p-2"
        >
          <ActionButton
            type="close"
            @action-click="emit('close')"
            class="body__closebtn"
          />
        </div>
        <div
          :class="[
            props.mode === 'desktop' ? 'py-3 px-4' : 'pb-3 px-6'
          ]"
        >
          <div class="border-b border-grey-100 pb-3 mb-3">
            <p
              v-if="props.config.gotoengine"
              class="border border-grey-100 rounded-full py-1 px-3 text-xs text-grey-500 mb-3 text-balance text-center leading-4"
            >
              {{ t('based on last visitor search') }}
            </p>
            <div class="flex items-baseline justify-between mb-1">
              <h2
                class="font-medium"
                :style="{ fontSize: styles.priceTitle.fontSize }"
              >
                {{ props.config.overrides.orBookWith || t('Book Direct') }}
              </h2>
              <DiscountPrice
                :data="discountPriceData"
                :size="styles.discountPrice.general.size"
                stacked
              />
            </div>
            <TruncateList
              v-if="props.config.numPerks"
              :data="props.config.perks"
              :show-more-text="t(
                'View all {num} perks',
                { num: props.config.numPerks },
              )"
              @show-more="emit('update:section', PriceSection.PERKS)"
              :style="{ fontSize: styles.perks.fontSize }"
              class="mb-3 text-grey-600 leading-4"
              data-testid="perks-list"
            >
              <template #default="{ item }">
                <Tooltip
                  :mode="isHoverCapable ? 'hover' : 'click'"
                  :disabled="!item.message"
                  :placement="tooltipPosition"
                  :z-index-tooltip="computedSettings.tooltipZIndex"
                  :container="computedSettings.tooltipContainer"
                  :nowrap="false"
                  :max-width="250"
                  role="tooltip"
                >
                  <template #trigger>
                    <span
                      :class="{ 'underline decoration-dashed decoration-grey-300': !!item.message }"
                    >
                      {{ item.translation ?? item.perk }}
                    </span>
                  </template>
                  <template #tooltip>
                    <p class="text-sm font-base">
                      {{ item.message }}
                    </p>
                  </template>
                </Tooltip>
              </template>
            </TruncateList>
            <Collapse
              v-if="priceMatchMessage && !props.config.gotoengine"
              v-model="showPriceMath"
            >
              <div class="priceMatch">
                <PriceMatchMsg
                  :message="priceMatchMessage"
                  :style="{ fontSize: styles.priceMatch.general.fontSize }"
                />
                <Button
                  v-if="priceMatchShowBtn && applyDiscountAction"
                  appearance="solid"
                  :color-scheme="{
                    main: theme.actionColor.base,
                    text: theme.actionColor.contrasted,
                    over: theme.actionColor.over,
                  }"
                  class="w-full mt-2"
                  :style="{ fontSize: styles.priceMatchBtn.fontSize }"
                  @click="handleApplyDiscount"
                >
                  {{ t('Apply discount') }}
                </Button>
              </div>
            </Collapse>
            <SaveSearchBtn
              v-if="props.config.showSaveSearch && !props.config.gotoengine"
              :style="{ fontSize: styles.saveSearchBtn.fontSize }"
              class="w-full"
              @click="emit('update:section', PriceSection.SAVE_SEARCH)"
            />
            <Button
              v-if="props.config.gotoengine"
              appearance="solid"
              :color-scheme="{
                main: theme.actionColor.base,
                text: theme.actionColor.contrasted,
                over: theme.actionColor.over,
              }"
              class="w-full mt-2"
              :style="{ fontSize: styles.saveSearchBtn.fontSize }"
              @click="openBEHandler"
            >
              <GotoBeBtnText :type="props.config.gotoengineBtnText" />
            </Button>
          </div>
          <RecordGrid
            :items="hotelPrices"
            direction="column"
            class="mb-2"
          >
            <template #name="{ item }">
              <span
                class="leading-5em"
                :style="{ fontSize: styles.hotelPrices.hotelName.fontSize }"
              >
                {{ item.name }}
              </span>
            </template>
            <template #value="{ item }">
              <span
                v-html="item.value"
                class="leading-5em"
                :style="{ fontSize: styles.hotelPrices.price.fontSize }"
              />
            </template>
          </RecordGrid>
          <ArrowBtn
            @click="emit('update:section', PriceSection.LEARN_MORE)"
            :style="{ fontSize: styles.learnMoreBtn.fontSize }"
          >
            {{ t('Learn more') }}
          </ArrowBtn>
        </div>
      </div>
      <section
        v-else-if="props.section === PriceSection.LEARN_MORE"
      >
        <FloatingSectionHeader
          :section="t('Learn more')"
          @back="emit('update:section', PriceSection.INDEX)"
          @close="emit('close')"
        />
        <div class="pt-4 px-8 pb-8">
          <LearnMore
            :show-contact="props.config.showContact"
            @contact="emit('update:section', PriceSection.CONTACT)"
          />
        </div>
        <Signature
          v-if="!props.config.overrides.orPowbyImg"
          :has-link="!props.config.overrides.orPowbyLink"
          :no-follow="props.config.overrides.orPowbyNofol"
        />
      </section>
      <section
        v-else-if="props.section === PriceSection.CONTACT"
      >
        <FloatingSectionHeader
          :section="t('Contact us')"
          @back="emit('update:section', PriceSection.LEARN_MORE)"
          @close="emit('close')"
        />
        <div
          v-if="contactEmailHandler"
          class="p-6 text-base"
        >
          <FormHandler
            :handler="contactEmailHandler"
          >
            <template #default="{ sending, handler }">
              <ContactForm
                :sending="sending"
                @success="handler"
              />
            </template>
            <template #confirmation>
              {{ t('Thank you for reaching out to us.') }}
            </template>
          </FormHandler>
        </div>
        <Signature
          v-if="!props.config.overrides.orPowbyImg"
          :has-link="!props.config.overrides.orPowbyLink"
          :no-follow="props.config.overrides.orPowbyNofol"
        />
      </section>
      <section
        v-else-if="props.section === PriceSection.SAVE_SEARCH"
      >
        <FloatingSectionHeader
          :section="t('Email this search')"
          @back="emit('update:section', PriceSection.INDEX)"
          @close="
            emit('close');
            resetSaveSearchValues();
          "
        />
        <div
          v-if="saveSearchHandler"
          class="p-6 text-base"
        >
          <FormHandler
            :handler="saveSearchHandler"
          >
            <template #default="{ sending, handler }">
              <EmailSearchForm
                :sending="sending"
                :initial-values="cacheSaveSearchValue"
                :terms-active="!!props.config.terms"
                :external-terms="!!props.config.terms?.isUrl"
                :terms-text="htmlEntityDecode(props.config.terms?.prcSsTermsLnk ?? '')"
                :terms-url="props.config.terms?.prcSsTermsText ?? ''"
                @success="handler"
                @terms="emit('update:section', PriceSection.TERMS)"
                @update-values="(values) => cacheSaveSearchValue = values"
              />
            </template>
            <template #confirmation>
              {{ t('Your search has been sent successfully.') }}
            </template>
          </FormHandler>
        </div>
      </section>
      <section
        v-else-if="props.section === PriceSection.TERMS"
      >
        <FloatingSectionHeader
          :section="t('Terms and Conditions')"
          @back="emit('update:section', PriceSection.SAVE_SEARCH)"
          @close="emit('close')"
        />
        <div
          class="pt-4 px-8 pb-8 max-h-96 overflow-auto"
        >
          <div
            v-html="props.config.terms?.prcSsTermsText"
          />
        </div>
        <Signature
          v-if="!props.config.overrides.orPowbyImg"
          :has-link="!props.config.overrides.orPowbyLink"
          :no-follow="props.config.overrides.orPowbyNofol"
        />
      </section>
      <section
        v-else-if="props.section === PriceSection.PERKS"
      >
        <FloatingSectionHeader
          :section="t('Perks')"
          @back="emit('update:section', PriceSection.INDEX)"
          @close="emit('close')"
        />
        <div
          class="py-2 px-6 max-h-96 overflow-auto"
        >
          <PerksList
            :items="props.config.perks"
          >
            <template #message="{ item }">
              <span class="text-xs">{{ item }}</span>
            </template>
          </PerksList>
        </div>
      </section>
    </Transition>
  </div>
</template>

<style scoped>
.vertical-content {
  color: v-bind(theme.fontColor);
}
.priceMatch {
  background-color: v-bind(theme.actionColor.washed);
  border-radius: 8px;
  @apply p-3 mb-2;
}
.actionBtn {
  color: v-bind(theme.actionColor.base);
  transition: color 0.3s ease;
}
.actionBtn:hover {
  color: v-bind(theme.actionColor.over);
}
</style>
